import {
  closeBranchesForJoin,
  ConductorTask,
  createForkJoinTask,
  createJoinTask
} from 'Automations/nodeDefinitions/conductor'
import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import {
  NodeType,
  ResolvedNodeDefinition,
  ResolvedNodeDefinitions
} from 'Automations/nodeDefinitions/types'
import { IntermediateNodeWithConditionalsInputsAndOutputs } from '../../presentationToConductorWorkflow.types'

export const intermediateNodeArrayToTaskDefinitions =
  (
    definitions: ResolvedNodeDefinitions,
    nodes: AutomationsPresentationV1Node[]
  ) =>
  (
    intermediateNodes: IntermediateNodeWithConditionalsInputsAndOutputs[],
    upstreamNodes: IntermediateNodeWithConditionalsInputsAndOutputs[] = []
  ): ConductorTask[] => {
    return intermediateNodes.flatMap((intermediateNode, nodeIndex) => {
      const { node, branches, branchHandles, inputs } = intermediateNode
      if (!node.type) throw new Error('type is undefined for node')

      const definition = definitions[node.type as NodeType]

      const upstreamNodesForThisNode = [
        ...upstreamNodes,
        ...intermediateNodes.slice(0, nodeIndex)
      ]

      const upstreamNodesForBranches = [
        ...upstreamNodesForThisNode,
        intermediateNode
      ]

      const handleMapping = definition.handleMapping?.({ config: node.data })

      /**
       * Single-handle node with multiple outgoing edges — all branches
       * run in parallel, wrapped in a FORK_JOIN + JOIN task pair.
       */
      if (branches.length > 0 && !handleMapping) {
        return transformParallelFork({
          intermediateNode,
          definition,
          upstreamNodesForThisNode,
          upstreamNodesForBranches,
          definitions,
          nodes
        })
      }

      /**
       * Multi-handle node (e.g. switch) where multiple edges share the same
       * handle — group branches by handle, then fork within each group.
       */
      if (branches.length > 0 && handleMapping && branchHandles) {
        const uniqueHandles = new Set(branchHandles)
        if (uniqueHandles.size < branchHandles.length) {
          return transformMappedFork({
            intermediateNode,
            definition,
            upstreamNodesForThisNode,
            upstreamNodesForBranches,
            branchHandles,
            definitions,
            nodes
          })
        }
      }

      /**
       * Default: each branch maps to a unique handle, so no parallel
       * forking is needed — pass branches directly to the task transformer.
       */
      return definition.taskTransformer({
        nodeId: node.id,
        config: node.data,
        inputMapping: inputs,
        upstreamNodes: upstreamNodesForThisNode,
        nodes,
        branches: branches.map(branch =>
          intermediateNodeArrayToTaskDefinitions(definitions, nodes)(
            branch,
            upstreamNodesForBranches
          )
        )
      })
    })
  }

type TransformBranchesContext = {
  intermediateNode: IntermediateNodeWithConditionalsInputsAndOutputs
  definition: ResolvedNodeDefinition<Record<string, unknown>>
  upstreamNodesForThisNode: IntermediateNodeWithConditionalsInputsAndOutputs[]
  upstreamNodesForBranches: IntermediateNodeWithConditionalsInputsAndOutputs[]
  definitions: ResolvedNodeDefinitions
  nodes: AutomationsPresentationV1Node[]
}

const FORK_JOIN_SUFFIX = 'PARALLEL'

const transformParallelFork = ({
  intermediateNode,
  definition,
  upstreamNodesForThisNode,
  upstreamNodesForBranches,
  definitions,
  nodes
}: TransformBranchesContext): ConductorTask[] => {
  const { node, branches, inputs } = intermediateNode

  const nodeTasks = definition.taskTransformer({
    nodeId: node.id,
    config: node.data,
    inputMapping: inputs,
    upstreamNodes: upstreamNodesForThisNode,
    nodes,
    branches: []
  })

  const transformedBranches = branches
    .map(branch =>
      intermediateNodeArrayToTaskDefinitions(definitions, nodes)(
        branch,
        upstreamNodesForBranches
      )
    )
    .filter(branch => branch.length > 0)

  if (transformedBranches.length === 0) {
    return nodeTasks
  }

  return [
    ...nodeTasks,
    ...wrapBranchesInForkJoin({
      nodeId: node.id,
      branches: transformedBranches,
      referenceSuffix: FORK_JOIN_SUFFIX
    })
  ]
}

const transformMappedFork = ({
  intermediateNode,
  definition,
  upstreamNodesForThisNode,
  upstreamNodesForBranches,
  branchHandles,
  definitions,
  nodes
}: TransformBranchesContext & {
  branchHandles: string[]
}): ConductorTask[] => {
  const { node, branches, inputs } = intermediateNode

  const branchesByHandle = groupBranchesByHandle(branches, branchHandles)

  const tasksByHandleGroup = branchesByHandle.map(
    ([handle, group], groupIndex) => {
      if (group.length === 1) {
        return intermediateNodeArrayToTaskDefinitions(definitions, nodes)(
          group[0],
          upstreamNodesForBranches
        )
      }

      const parallelBranches = group
        .map(branch =>
          intermediateNodeArrayToTaskDefinitions(definitions, nodes)(
            branch,
            upstreamNodesForBranches
          )
        )
        .filter(branch => branch.length > 0)

      if (parallelBranches.length === 0) {
        return []
      }

      const forkJoinTasks = wrapBranchesInForkJoin({
        nodeId: node.id,
        branches: parallelBranches,
        referenceSuffix: `${FORK_JOIN_SUFFIX}:${groupIndex}`
      })

      const groupContinuation = intermediateNode.groupContinuations?.[handle]
      if (groupContinuation) {
        const continuationTasks = intermediateNodeArrayToTaskDefinitions(
          definitions,
          nodes
        )(groupContinuation, upstreamNodesForBranches)
        return [...forkJoinTasks, ...continuationTasks]
      }

      return forkJoinTasks
    }
  )

  return definition.taskTransformer({
    nodeId: node.id,
    config: node.data,
    inputMapping: inputs,
    upstreamNodes: upstreamNodesForThisNode,
    nodes,
    branches: tasksByHandleGroup
  })
}

const wrapBranchesInForkJoin = ({
  nodeId,
  branches,
  referenceSuffix
}: {
  nodeId: string
  branches: ConductorTask[][]
  referenceSuffix: string
}): ConductorTask[] => {
  const { branches: joinableBranches, joinOn } = closeBranchesForJoin(branches)

  return [
    createForkJoinTask({
      name: 'parallel-fork',
      taskReferenceName: `${nodeId}:${referenceSuffix}:fork`,
      forkTasks: joinableBranches,
      inputParameters: {}
    }),
    createJoinTask({
      name: 'parallel-join',
      taskReferenceName: `${nodeId}:${referenceSuffix}:join`,
      joinOn,
      inputParameters: {}
    })
  ]
}

type HandleGroup = [
  handle: string,
  branches: IntermediateNodeWithConditionalsInputsAndOutputs[][]
]

const groupBranchesByHandle = (
  branches: IntermediateNodeWithConditionalsInputsAndOutputs[][],
  branchHandles: string[]
): HandleGroup[] => {
  const groups = branchHandles.reduce((acc, handle, index) => {
    const existing = acc.get(handle) ?? []
    return new Map(acc).set(handle, [...existing, branches[index]])
  }, new Map<string, IntermediateNodeWithConditionalsInputsAndOutputs[][]>())

  return [...groups.entries()]
}
